import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import partialRight from 'lodash/partialRight';
import { PapiComment } from '@wix/da-papi-types';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { BiData, ClickOnCommentsToStartTypingBiEvent } from '@wix/da-bi/pkg/events';
import { LoadableComponentContext } from '@wix/da-hooks/pkg/useLoadableComponent';
import {
  useEditorDraftUnmountEffects,
  useEditorDraftState,
} from '@wix/da-editor-tiptap/pkg/clientDrafts/hooks';
import { EditorContext } from '@wix/da-editor-tiptap/pkg/components/Editor/Context';
import type { EditorState } from '@wix/da-editor-tiptap/pkg/components/Editor/types';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { CommentableTypes } from '../../Comment/redux/types';
import {
  getPublishResult,
  isBusy as isBusySelector,
  getShouldResetEditor,
} from '../redux/selectors';
import { CommentEditorSource, CommentPublisherResult } from '../redux/types';
import { usePublishSuccessCleanup } from './usePublishSuccessCleanup';
import { useForceLoadIfNeeded } from './useForceLoadIfNeeded';
import { useShouldFocus } from './useShouldFocus';
import { focusDone } from '../redux/actionCreators';

export interface UseCommentEditorOptions {
  editorSource: CommentEditorSource;
  relevantComment?: PapiComment;

  initiallyExpanded?: boolean;

  onPublishSuccess?: (result: CommentPublisherResult) => void;
}

export interface UseCommentEditorValue {
  isExpanded: boolean;

  /** `true` if we're waiting on a publish request to finish. */
  isBusy: boolean;
  /** Should the comment editor be cleared after this result? */
  shouldResetEditor?: boolean;

  /** `true` if we were manually requested to move focus to the editor. */
  shouldFocus: boolean;

  /**
   * What the source/purpose of the editor is. Refer to {@link CommentEditorSource} for
   * more information on the supported values.
   */
  editorSource: CommentEditorSource;

  /**
   * The comment that's relevant to the {@link editorSource}.
   *
   * Will be equal to one of the following:
   * - The comment we're replying to
   * - The comment we're editing.
   * - `null`
   */
  relevantComment?: PapiComment;

  /**
   * We need to track the implementation's root element to help manage focus.
   *
   * E.g. when a "cancel" button is pressed, but the editor sticks around in a collapsed state, where do we drop user's
   * focus? We can't leave them on the invisible "cancel" button. The root seems like a good call here.
   */
  rootElementRef?: React.RefObject<HTMLDivElement | null>;

  /**
   * The initial content of the editor.
   */
  initialContent?: EditorState;

  /** Expands the editor. Also triggers the load on the nearest {@link LoadableComponentContext}. */
  expandEditor: () => void;

  /** Collapses the editor. */
  collapseEditor: () => void;
}

/** Provides all the state and effects you'll need for a Comment editor! */
export function useCommentEditorSpecifics(
  commentBoxId: string,
  commentableItemId: string | number,
  commentableType: CommentableTypes,
  {
    editorSource,
    relevantComment,
    initiallyExpanded,
    onPublishSuccess,
  }: UseCommentEditorOptions = {
    editorSource: CommentEditorSource.Independent,
  }
): UseCommentEditorValue {
  const rootElementRef = useRef<HTMLDivElement | null>(null);
  const shouldFocus = useShouldFocus(commentBoxId);
  const logBiEvent = useBiLogger();
  const { triggerLoad } = useContext(LoadableComponentContext);
  const [isExpanded, setIsExpanded] = useState(
    initiallyExpanded || shouldFocus || editorSource === CommentEditorSource.Edit
  );

  // Handle everything for expanding a comment box:
  //  - Logging BI
  //  - Updating the loading context so that our CommentEditor chunk gets loaded
  //  - Updating the local state's isExpanded indicator
  const expandEditor = useCallback(() => {
    if (!isExpanded) {
      logBiEvent(
        BiData<ClickOnCommentsToStartTypingBiEvent>({
          evid: 183,
          itemid:
            typeof commentableItemId === 'string' ? parseInt(commentableItemId) : commentableItemId,
          typeid: commentableType,
        })
      );
      triggerLoad();
      setIsExpanded(true);
    }
  }, [logBiEvent, commentableItemId, commentableType, triggerLoad, isExpanded]);

  const collapseEditor = useCallback(() => setIsExpanded(false), []);

  const editorDraftState = useEditorDraftState(commentBoxId);

  // We need to figure out what the content of the comment box should be on mount. There are two options:
  //  1. A draft that the user started, but left the page
  //  2. The content of an existing comment that the user is editing.
  let initialContent: any = null;
  if (editorDraftState) {
    initialContent = editorDraftState;
  } else if (editorSource === CommentEditorSource.Edit && relevantComment) {
    initialContent = relevantComment.textContent;
  }

  useCommentEditorEffects({
    editorId: commentBoxId,
    shouldFocus,
    expandEditor,
    collapseEditor,
    onPublishSuccess,
    editorSource,
    hasInitialContent: !!initialContent,
  });

  // Now some values from redux we pass straight through
  const isBusy: boolean = useSelector(partialRight(isBusySelector, commentBoxId));
  const shouldResetEditor: boolean | undefined = useSelector(
    partialRight(getShouldResetEditor, commentBoxId)
  );

  return {
    editorSource,
    rootElementRef,
    isBusy,
    shouldResetEditor,
    relevantComment,
    shouldFocus,
    isExpanded,
    expandEditor,
    collapseEditor,
    initialContent,
  };
}

/**
 * Handles all the effects that a comment editor needs to handle.
 *
 * Some of what it handles:
 *  - Draft saving/deleting
 *  - Error handling
 *  - Expand on focus
 *  - Collapse on publish
 *  - and more!
 */
function useCommentEditorEffects({
  editorId,
  shouldFocus,
  collapseEditor,
  expandEditor,
  onPublishSuccess,
  editorSource,
  hasInitialContent,
}) {
  const { ref: editor, isReady: isEditorReady } = useContext(EditorContext);
  useEditorDraftUnmountEffects(editorId);

  const publishResult = useSelector(partialRight(getPublishResult, editorId));
  // Make sure we trigger onPublishSuccess if publishResult comes back successful
  // TODO: Run this in a saga somewhere instead?
  usePublishSuccessCleanup(editorId, publishResult, onPublishSuccess);

  // Collapse the editor after the user successfully publishes a comment.
  useEffect(() => {
    if (publishResult) {
      collapseEditor();
    }
  }, [publishResult, collapseEditor]);

  // Expand the editor if it's ever focused.
  useEffect(() => {
    if (editor) {
      const onFocus = () => expandEditor();
      editor.on('focus', onFocus);
      return () => {
        editor.off('focus', onFocus);
      };
    }
  }, [editor, expandEditor]);

  // Focus the editor if requested and clear out the request.
  const dispatchFocusDone = useAction(focusDone);
  useEffect(() => {
    if (shouldFocus && editor && isEditorReady) {
      const onFocus = () => {
        dispatchFocusDone(editorId);
      };
      editor.on('focus', onFocus);

      editor.commands.focus();

      return () => {
        editor.off('focus', onFocus);
      };
    }
  }, [editorId, expandEditor, shouldFocus, isEditorReady, editor, dispatchFocusDone]);

  useForceLoadIfNeeded(editorSource, shouldFocus, hasInitialContent);
}
